import { fetcher, queryString, useRecoilQuery } from '@shared/hooks/recoil-query';
import { LowerRole } from '@shared/types';

import {
  giftOrderForAdminDataAtomFamily,
  giftOrderForAdminStatusAtomFamily,
  giftOrderForStoreDataAtomFamily,
  giftOrderForStoreStatusAtomFamily,
  giftOrderListForAdminDataAtomFamily,
  giftOrderListForAdminStatusAtomFamily,
  giftOrderListForBugoAgencyAdminDataAtom,
  giftOrderListForBugoAgencyAdminStatusAtom,
  giftOrderListForStoreDataAtomFamily,
  giftOrderListForStoreStatusAtomFamily,
  giftOrderSearchDataAtomFamily,
  giftOrderSearchStatusAtomFamily,
} from './giftOrder.atom';
import { GiftOrderPaymentStatus } from './giftOrder.interface';

// Populate 옵션
const giftOrderPopulate = [
  {
    path: 'bugo',
    populate: [
      { path: 'member', select: 'fullName phoneNumber' },
      { path: 'fevent', select: 'deceasedInfo' },
    ],
  },
  { path: 'bugoBrand', select: 'bugoAgency' },
  { path: 'user', select: 'info' },
  { path: 'fevent', select: 'deceasedInfo' },
  {
    path: 'orderDetails.giftItem',
    populate: [{ path: 'giftItemInfo' }, { path: 'image' }],
  },
  {
    path: 'coupons',
    select:
      'code seq shareStatus redeemStatus sharedAt viewedAt selectedChoiceUnit recipient gifticon purchasedAt expiresAt refund',
    populate: [{ path: 'selectedChoiceUnit', select: 'name image description' }],
  },
];

// 결제완료된 GiftOrder 목록 조회 (Admin용)
const giftOrderListForAdminUrl = (filter: Record<string, unknown> = {}) => {
  const defaultFilter = {
    'paymentDetail.status': {
      $in: [GiftOrderPaymentStatus.Paid, GiftOrderPaymentStatus.Confirmed],
    },
  };
  const mergedFilter = { ...defaultFilter, ...filter };

  return `/${LowerRole.Admin}/gift-order?${queryString({
    filter: mergedFilter,
    populate: giftOrderPopulate,
    options: { sort: { createdAt: -1 } },
  })}`;
};

export const useGiftOrderListForAdminHook = (
  filterKey = 'default',
  filter: Record<string, unknown> = {},
  skip = false,
) => {
  useRecoilQuery(
    giftOrderListForAdminStatusAtomFamily(filterKey),
    giftOrderListForAdminDataAtomFamily(filterKey),
    giftOrderListForAdminUrl(filter),
    fetcher,
    skip,
  );
};

// 결제완료된 GiftOrder 목록 조회 (BugoAgencyAdmin용)
const giftOrderListForBugoAgencyAdminUrl = (filter: Record<string, unknown> = {}) => {
  const defaultFilter = {
    'paymentDetail.status': {
      $in: [GiftOrderPaymentStatus.Paid, GiftOrderPaymentStatus.Confirmed],
    },
  };
  const mergedFilter = { ...defaultFilter, ...filter };

  return `/${LowerRole.BugoAgencyAdmin}/gift-order?${queryString({
    filter: mergedFilter,
    populate: giftOrderPopulate,
    options: { sort: { createdAt: -1 } },
  })}`;
};

export const useGiftOrderListForBugoAgencyAdminHook = (
  filterKey = 'default',
  filter: Record<string, unknown> = {},
  skip = false,
) => {
  useRecoilQuery(
    giftOrderListForBugoAgencyAdminStatusAtom(filterKey),
    giftOrderListForBugoAgencyAdminDataAtom(filterKey),
    giftOrderListForBugoAgencyAdminUrl(filter),
    fetcher,
    skip,
  );
};

// GiftOrder 검색 (Admin용)
export interface GiftOrderSearchParams {
  phoneNumber?: string;
  receiverName?: string;
}

const giftOrderSearchUrl = (params: GiftOrderSearchParams) => {
  const queryParams: Record<string, unknown> = {
    populate: giftOrderPopulate,
  };

  // receiverName, phoneNumber는 개별 query param으로 전달
  if (params.receiverName) {
    queryParams.receiverName = params.receiverName;
  }
  if (params.phoneNumber) {
    queryParams.phoneNumber = params.phoneNumber;
  }

  return `/${LowerRole.Admin}/gift-order/search?${queryString(queryParams)}`;
};

export const useGiftOrderSearchHook = (
  searchKey: string,
  params: GiftOrderSearchParams,
  skip = false,
) => {
  useRecoilQuery(
    giftOrderSearchStatusAtomFamily(searchKey),
    giftOrderSearchDataAtomFamily(searchKey),
    giftOrderSearchUrl(params),
    fetcher,
    skip,
  );
};

// 단일 GiftOrder 조회 (Admin용)
const giftOrderForAdminUrl = (orderId: string) => {
  return `/${LowerRole.Admin}/gift-order/${orderId}?${queryString({
    populate: JSON.stringify(giftOrderPopulate),
  })}`;
};

export const useGiftOrderForAdminHook = (orderId: string, skip = false) => {
  useRecoilQuery(
    giftOrderForAdminStatusAtomFamily(orderId),
    giftOrderForAdminDataAtomFamily(orderId),
    giftOrderForAdminUrl(orderId),
    fetcher,
    skip || !orderId,
  );
};

// ========== Store용 (StoreCustomer, StoreGuest) ==========

// Store용 populate 옵션 (간소화)

// 본인 주문 목록 조회 (Store용)
const giftOrderListForStoreUrl = (role: LowerRole) => {
  return `/${role}/gift-order?${queryString({
    options: { sort: { createdAt: -1 } },
  })}`;
};

export const useGiftOrderListForStoreHook = (role: LowerRole, skip = false) => {
  useRecoilQuery(
    giftOrderListForStoreStatusAtomFamily(role),
    giftOrderListForStoreDataAtomFamily(role),
    giftOrderListForStoreUrl(role),
    fetcher,
    skip,
  );
};

// 단일 GiftOrder 조회 (Store용)
const giftOrderForStoreUrl = (orderId: string, role: LowerRole) => {
  return `/${role}/gift-order/${orderId}`;
};

export const useGiftOrderForStoreHook = (
  orderId: string,
  role: LowerRole,
  skip = false,
) => {
  useRecoilQuery(
    giftOrderForStoreStatusAtomFamily(orderId),
    giftOrderForStoreDataAtomFamily(orderId),
    giftOrderForStoreUrl(orderId, role),
    fetcher,
    skip || !orderId,
  );
};
